// common
import {
  getModule,
  VuexModule,
  Mutation,
  Module,
} from "vuex-module-decorators";
import store from "@/store";
import {
  ICashFloat,
  ICashFloatState,
  IPreviousCashfloat,
} from "@/interfaces/cash-float/cash-float";
import { LogService } from "@/services/logging";
import { Currencies } from "@/constants/currency";
import POSConfigModule from "./pos-config";

@Module({
  dynamic: true,
  namespaced: true,
  store,
  name: "CashFloatConfig",
})
class CashFloatConfig extends VuexModule implements ICashFloatState {
  // public userId = null;
  // public posId = null;
  // public groupId = null;
  // public startAmount = 0;
  // public amount = 0;
  // public userSwitch = [];
  // public closeDate = "";
  // public createdAt = "";
  // public updatedAt = "";
  // public uid = "";

  cashFloat: ICashFloat | null = null;
  public transfersAmount = 0;
  public sumOfCashIn = 0;
  public sumOfCashOut = 0;
  public isClosingCashfloat = false;
  public isOpeningCashfloat = false;
  public showClosingSummary = false;
  // used to track if gain/loss transfer has already been done on closure, to not repeat it in case the transfer after it fails to synchronize
  public initialTransferDone = false;

  // tracks which currencies' difference transfers already synced for a V2
  // closure or opening, so a retried submit only re-fires the currencies
  // that failed. Keyed by the cashfloat uid. The field name predates FIS-553
  // and is kept: it is persisted to localStorage, so renaming it would drop
  // the guard for a device that updates the app mid-closure.
  public gainLossSyncedCurrencies: {
    cashfloatUid: string;
    currencies: string[];
  } | null = null;

  // FIS-752: closure transfers synced, cents by "<paymentMethod>:<currency>"
  public closureTransfersSynced: {
    cashfloatUid: string;
    locationId: number;
    amounts: Record<string, number>;
  } | null = null;

  public previousCashfloat: IPreviousCashfloat | null = null;

  get getCashFloat() {
    return this.cashFloat;
  }

  get getTransfersAmount() {
    return this.transfersAmount;
  }

  get getSumOfCashIn() {
    return this.sumOfCashIn;
  }

  get getSumOfCashOut() {
    return this.sumOfCashOut;
  }

  get getPreviousCashfloat() {
    return this.previousCashfloat;
  }

  // Group a closed session left cash in; the device stays on it until that cash is cashed out
  get drawerCashGroupId(): number | null {
    const previous = this.previousCashfloat;
    if (!previous || previous.isCashedOut) {
      return null;
    }
    const hasCash = Object.values(previous.closingAmounts || {}).some(
      (amount) => (amount || 0) > 0
    );
    if (!hasCash) {
      return null;
    }
    // Records saved before FIS-552 have no group: fall back to the device's last POS config
    return previous.groupId || POSConfigModule.selectedConfig?.group || null;
  }

  @Mutation
  public setShowClosingSummary(value: boolean) {
    this.showClosingSummary = value;
  }

  @Mutation
  public updateTransfersAmount(newCashTransfer: number) {
    this.transfersAmount = this.transfersAmount + newCashTransfer;
  }

  @Mutation
  public updateCashFloatAmount(payload: {
    currency: string;
    deltaCents: number;
  }) {
    if (!this.cashFloat?.amounts) return;
    const curr = payload.currency as Currencies;
    this.cashFloat.amounts[curr] =
      (this.cashFloat.amounts[curr] ?? 0) + payload.deltaCents;
  }

  @Mutation
  public setTransfersAmount(newCashTransfer: number) {
    this.transfersAmount = newCashTransfer;
  }

  @Mutation
  public updateSumOfCashIn(newCashIn: number) {
    LogService.info(
      `Updating SumOfCashIn from ${this.sumOfCashIn} to ${
        this.sumOfCashIn + newCashIn
      }`
    );
    this.sumOfCashIn = this.sumOfCashIn + newCashIn;
  }

  @Mutation
  public setSumOfCashIn(newCashIn: number) {
    LogService.info(`Setting SumOfCashIn to ${newCashIn}`);
    this.sumOfCashIn = newCashIn;
  }

  @Mutation
  public updateSumOfCashOut(newCashOut: number) {
    LogService.info(
      `Updating SumOfCashOut from ${this.sumOfCashOut} to ${
        this.sumOfCashOut + newCashOut
      }`
    );
    this.sumOfCashOut = this.sumOfCashOut + newCashOut;
  }

  @Mutation
  public setSumOfCashOut(newCashOut: number) {
    LogService.info(`Setting SumOfCashOut to ${newCashOut}`);
    this.sumOfCashOut = newCashOut;
  }

  @Mutation
  public setIsClosingCashfloat(value: boolean) {
    this.isClosingCashfloat = value;
  }

  @Mutation
  public setIsOpeningCashfloat(value: boolean) {
    this.isOpeningCashfloat = value;
  }

  @Mutation
  public setCashFloat(cashFloat: ICashFloat | null) {
    this.cashFloat = cashFloat;
  }

  @Mutation
  public setPreviousCashFloat(previousCashfloat: IPreviousCashfloat | null) {
    this.previousCashfloat = previousCashfloat;
  }

  @Mutation
  public setInitialTransferDone(value: boolean) {
    this.initialTransferDone = value;
  }

  @Mutation
  public addGainLossSyncedCurrency(payload: {
    cashfloatUid: string;
    currency: string;
  }) {
    // reassign instead of mutating in place so the persistence watchEffect
    // reliably picks up the change
    if (this.gainLossSyncedCurrencies?.cashfloatUid !== payload.cashfloatUid) {
      this.gainLossSyncedCurrencies = {
        cashfloatUid: payload.cashfloatUid,
        currencies: [payload.currency],
      };
      return;
    }
    if (!this.gainLossSyncedCurrencies.currencies.includes(payload.currency)) {
      this.gainLossSyncedCurrencies = {
        cashfloatUid: payload.cashfloatUid,
        currencies: [
          ...this.gainLossSyncedCurrencies.currencies,
          payload.currency,
        ],
      };
    }
  }

  @Mutation
  public clearGainLossSyncedCurrencies() {
    this.gainLossSyncedCurrencies = null;
  }

  @Mutation
  public addClosureTransferSynced(payload: {
    cashfloatUid: string;
    locationId: number;
    key: string;
    amount: number;
  }) {
    const current =
      this.closureTransfersSynced?.cashfloatUid === payload.cashfloatUid
        ? this.closureTransfersSynced.amounts
        : {};
    if (current[payload.key] !== undefined) {
      return;
    }
    this.closureTransfersSynced = {
      cashfloatUid: payload.cashfloatUid,
      locationId: payload.locationId,
      amounts: { ...current, [payload.key]: payload.amount },
    };
  }

  @Mutation
  public clearClosureTransfersSynced() {
    this.closureTransfersSynced = null;
  }

  @Mutation
  public restore(moduleFields: Partial<ICashFloatState>) {
    const {
      cashFloat,
      transfersAmount,
      sumOfCashIn,
      sumOfCashOut,
      previousCashfloat,
      initialTransferDone,
      gainLossSyncedCurrencies,
      closureTransfersSynced,
    } = moduleFields;
    if (cashFloat) {
      // If no version is set, ensure that we multiply the amount by 100
      // to make values compatible with what the backend expects
      // we use the groupId to check if the cashFloat is not just an empty object
      if (!cashFloat.version && cashFloat.groupId) {
        if (cashFloat.amounts) {
          for (const currency in cashFloat.amounts) {
            // eslint-disable-next-line no-prototype-builtins
            if (cashFloat.amounts.hasOwnProperty(currency)) {
              cashFloat.amounts[currency as Currencies] =
                cashFloat.amounts[currency as Currencies]! * 100;
            }
          }
        }
        if (cashFloat.startAmounts) {
          for (const currency in cashFloat.startAmounts) {
            // eslint-disable-next-line no-prototype-builtins
            if (cashFloat.startAmounts.hasOwnProperty(currency)) {
              cashFloat.startAmounts[currency as Currencies] =
                cashFloat.startAmounts[currency as Currencies]! * 100;
            }
          }
        }
        if (cashFloat.userSwitch && cashFloat.userSwitch.length) {
          cashFloat.userSwitch.forEach((userSwitch) => {
            if (userSwitch.amounts) {
              for (const curr in userSwitch.amounts) {
                const currency = curr as Currencies;
                userSwitch.amounts[currency] =
                  (userSwitch.amounts[currency] || 0) * 100;
              }
            }
            if (userSwitch.validAmounts) {
              for (const curr in userSwitch.validAmounts) {
                const currency = curr as Currencies;
                userSwitch.validAmounts[currency] =
                  (userSwitch.validAmounts[currency] || 0) * 100;
              }
            }
          });
        }
        if (initialTransferDone) {
          this.initialTransferDone = initialTransferDone;
        }
        cashFloat.version = 1;
      }
      // before setting cash float, migrate new fields added for multi currency work
      const groupCurrency =
        (POSConfigModule.groupInfo?.currency as Currencies) || Currencies.CHF;

      if (!cashFloat.amounts) {
        cashFloat.amounts = {};
        cashFloat.amounts[groupCurrency] = cashFloat.amount;
      }
      if (!cashFloat.startAmounts) {
        cashFloat.startAmounts = {};
        cashFloat.startAmounts[groupCurrency] = cashFloat.startAmount;
      }
      if (cashFloat.message && !cashFloat.messages) {
        cashFloat.messages = {};
        cashFloat.messages[groupCurrency] = cashFloat.message;
      }
      if (cashFloat.totalCashSales && !cashFloat.totalCashSalesByCurrency) {
        cashFloat.totalCashSalesByCurrency = {};
        cashFloat.totalCashSalesByCurrency[groupCurrency] =
          cashFloat.totalCashSales;
      }
      if (cashFloat.validAmount && !cashFloat.validAmounts) {
        cashFloat.validAmounts = {};
        cashFloat.validAmounts[groupCurrency] = cashFloat.validAmount;
      }
      if (cashFloat.cashDifference && !cashFloat.cashDifferences) {
        cashFloat.cashDifferences = {};
        cashFloat.cashDifferences[groupCurrency] = cashFloat.cashDifference;
      }
      if (cashFloat.billsAndCoins && !cashFloat.billsAndCoinsByCurrency) {
        cashFloat.billsAndCoinsByCurrency = {};
        if (cashFloat.billsAndCoins.opening) {
          cashFloat.billsAndCoinsByCurrency.opening = [
            cashFloat.billsAndCoins.opening,
          ];
        }
        if (cashFloat.billsAndCoins.closing) {
          cashFloat.billsAndCoinsByCurrency.closing = [
            cashFloat.billsAndCoins.closing,
          ];
        }
      }
      this.cashFloat = cashFloat;
    }
    if (transfersAmount) {
      this.transfersAmount = transfersAmount;
    }
    if (sumOfCashIn) {
      this.sumOfCashIn = sumOfCashIn;
    }
    if (sumOfCashOut) {
      this.sumOfCashOut = sumOfCashOut;
    }
    if (previousCashfloat) {
      this.previousCashfloat = previousCashfloat;
    }
    // restored unconditionally (not only for legacy no-version cashfloats):
    // a close retried after an app restart must still skip the currencies
    // whose gain/loss transfer already synced
    if (gainLossSyncedCurrencies) {
      this.gainLossSyncedCurrencies = gainLossSyncedCurrencies;
    }
    if (closureTransfersSynced) {
      this.closureTransfersSynced = closureTransfersSynced;
    }
  }
}
const CashFloatModule = getModule(CashFloatConfig);
export default CashFloatModule;
